import { Link, useRouterState } from '@tanstack/react-router';
import { useEffect, useId, useRef, useState } from 'react';
import { IconButton } from '@/components/shared/IconButton';
import { RetracedMark } from '@/components/shared/RetracedMark';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useCanAccessEntityScreening } from '@/hooks/useCanAccessEntityScreening';
import { useFeatures } from '@/hooks/useFeatures';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useTranslator } from '@/hooks/useTranslator';
import { cn } from '@/lib/utils/cn';
import { buildNav } from '@/lib/utils/sidebarNavHelpers';
import { useSidebarStore } from '@/store/sidebarStore';
import { StripGroup } from './StripGroup';
import { StripItem } from './StripItem';
import type { NavGroup } from './types';

// ---------- Styles ----------

// Selected nav item: teal text on a slight light-turquoise (brand-accent) wash.
// Second-layer panel sits on a dark-teal (primary) background with inverted
// text. Selected item = softened turquoise pill (lower contrast on teal).
const activeCls =
  'bg-brand-accent/30 text-sidebar-foreground font-medium ring-1 ring-inset ring-brand-accent/40';
const inactiveCls =
  'text-sidebar-foreground/85 hover:bg-brand-accent/25 hover:text-sidebar-foreground';

function isPathActive(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

// ---------- Root ----------

export default function Sidebar() {
  const pathname = useRouterState({ select: s => s.location.pathname });
  const features = useFeatures();
  const { getTranslation } = useTranslator({ page: 'sidebar' });
  const canAccessEntityScreening = useCanAccessEntityScreening();
  const nav = buildNav(features, getTranslation, canAccessEntityScreening);

  const selectedGroupKey = useSidebarStore(s => s.selectedGroupKey);
  const panelOpen = useSidebarStore(s => s.panelOpen);
  const mobileOpen = useSidebarStore(s => s.mobileOpen);
  const setSelectedGroupKey = useSidebarStore(s => s.setSelectedGroupKey);
  const setPanelOpen = useSidebarStore(s => s.setPanelOpen);
  const togglePanel = useSidebarStore(s => s.togglePanel);
  const setMobileOpen = useSidebarStore(s => s.setMobileOpen);
  const isMobile = useIsMobile();

  const sidebarId = useId();
  const [hoveredGroupKey, setHoveredGroupKey] = useState<string | null>(null);

  // Hover-intent: a short open delay (so the panel doesn't pop on a quick
  // pass-over), and a close delay that bridges the gap as the cursor moves from
  // the rail icon into the panel so it doesn't flicker.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openPanel = (key: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (openTimer.current) clearTimeout(openTimer.current);
    openTimer.current = setTimeout(() => setHoveredGroupKey(key), 120);
  };
  const keepPanelOpen = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };
  const scheduleClosePanel = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setHoveredGroupKey(null), 200);
  };
  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
      if (openTimer.current) clearTimeout(openTimer.current);
    },
    [],
  );

  useEffect(() => {
    const groups = nav.filter((e): e is NavGroup => e.type === 'group');
    for (const group of groups) {
      const visible = group.children.filter(c => !c.visible || c.visible());
      if (visible.some(c => isPathActive(pathname, c.matchPrefix ?? c.href))) {
        setSelectedGroupKey(group.key);
        return;
      }
    }
    setSelectedGroupKey(null);
  }, [pathname, nav, setSelectedGroupKey]);

  // When the panel is NOT pinned, collapse the hover-opened flyout as soon as
  // the route changes — otherwise it lingers after you've navigated to a page.
  // Keyed to pathname only (panelOpen is read but must not retrigger this).
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally runs on navigation only
  useEffect(() => {
    if (panelOpen) return;
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setHoveredGroupKey(null);
  }, [pathname]);

  const displayKey = panelOpen ? selectedGroupKey : hoveredGroupKey;
  const displayGroup = nav.find(
    (e): e is NavGroup => e.type === 'group' && e.key === displayKey,
  );
  const visibleChildren =
    displayGroup?.children.filter(c => !c.visible || c.visible()) ?? [];
  const showPanel = displayGroup != null && visibleChildren.length > 0;

  function renderPanel(overlay: boolean) {
    if (!displayGroup || visibleChildren.length === 0) return null;
    return (
      <nav
        className={cn(
          'flex flex-col overflow-hidden bg-sidebar text-sidebar-foreground',
          overlay
            ? 'absolute left-16 top-0 bottom-0 w-50 z-50 border-r border-sidebar-foreground/10 animate-in fade-in slide-in-from-left-3 duration-300 ease-out'
            : 'flex-1 min-w-0',
        )}
        onMouseEnter={() => !panelOpen && keepPanelOpen()}
        onMouseLeave={() => !panelOpen && scheduleClosePanel()}
      >
        <div className='flex-1 overflow-y-auto pt-3 pb-3 px-2 flex flex-col'>
          <div className='flex items-center justify-between px-2 pt-0.5 pb-3'>
            <p className='text-xs font-semibold tracking-[0.04em] text-sidebar-foreground/70 select-none'>
              {displayGroup.label}
            </p>
            <IconButton
              variant='ghost'
              size='icon'
              tooltip={
                panelOpen
                  ? getTranslation('unpinPanel')
                  : getTranslation('pinPanel')
              }
              className='w-5 h-5 text-sidebar-foreground/70 hover:text-sidebar-foreground! hover:bg-brand-accent/25'
              onClick={() => {
                if (panelOpen) {
                  togglePanel();
                } else {
                  setSelectedGroupKey(hoveredGroupKey);
                  setPanelOpen(true);
                }
              }}
            >
              <i
                className={cn(
                  'fa-regular text-xs',
                  panelOpen ? 'fa-sidebar-flip' : 'fa-thumbtack',
                )}
              />
            </IconButton>
          </div>
          <div className='flex flex-col gap-1'>
            {visibleChildren.map(child => {
              const active = isPathActive(
                pathname,
                child.matchPrefix ?? child.href,
              );
              return (
                <Link
                  key={child.key}
                  to={child.href as never}
                  onClick={() => isMobile && setMobileOpen(false)}
                  className={cn(
                    'flex items-center gap-2 px-2.5 py-2 rounded-md text-sm transition-colors cursor-pointer',
                    active ? activeCls : inactiveCls,
                  )}
                >
                  {child.label}
                </Link>
              );
            })}
          </div>
        </div>
      </nav>
    );
  }

  // On mobile: inline panel always (no overlay); width driven by panel visibility
  const mobilePanelOpen = isMobile && showPanel;
  const sidebarWidth = isMobile
    ? mobilePanelOpen
      ? 'w-72'
      : 'w-16'
    : undefined;

  const sidebarContent = (
    <aside
      id={sidebarId}
      className={cn(
        'relative flex h-full bg-sidebar text-sidebar-foreground transition-[width] duration-300 ease-out',
        sidebarWidth,
      )}
    >
      {/* Left strip */}
      <TooltipProvider delay={500}>
        <nav
          className={cn(
            'shrink-0 flex flex-col pt-5 pb-1 gap-3 overflow-y-auto overflow-x-hidden w-16 px-1.5',
            panelOpen && showPanel && 'border-r border-sidebar-foreground/10',
          )}
        >
          <div className='flex justify-center pb-1'>
            <RetracedMark className='size-7 text-sidebar-foreground' />
          </div>
          <div className='flex-1 flex flex-col gap-3'>
            {nav.map(entry => {
              if (entry.type === 'section') return null;
              if (entry.type === 'item') {
                const active =
                  pathname === entry.href ||
                  pathname.startsWith(`${entry.href}/`) ||
                  (entry.key === 'dashboard' && pathname === '/');
                return (
                  <StripItem
                    key={entry.key}
                    item={entry}
                    active={active}
                    onNavigate={
                      isMobile ? () => setMobileOpen(false) : undefined
                    }
                  />
                );
              }
              const visible = entry.children.filter(
                c => !c.visible || c.visible(),
              );
              if (visible.length === 0) return null;
              const anyChildActive = visible.some(c =>
                isPathActive(pathname, c.matchPrefix ?? c.href),
              );
              return (
                <StripGroup
                  key={entry.key}
                  group={entry}
                  selected={selectedGroupKey === entry.key}
                  anyChildActive={anyChildActive}
                  {...(visible[0]?.href
                    ? { firstChildHref: visible[0].href }
                    : {})}
                  onMouseEnter={() => !panelOpen && openPanel(entry.key)}
                  onMouseLeave={() => !panelOpen && scheduleClosePanel()}
                  onClick={() => {
                    if (panelOpen && selectedGroupKey === entry.key) {
                      togglePanel();
                    } else {
                      setSelectedGroupKey(entry.key);
                      setPanelOpen(true);
                    }
                  }}
                />
              );
            })}
          </div>
        </nav>
      </TooltipProvider>

      {isMobile
        ? showPanel && renderPanel(false)
        : panelOpen
          ? renderPanel(false)
          : showPanel && renderPanel(true)}
    </aside>
  );

  if (isMobile) {
    return (
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side='left'
          showCloseButton={false}
          className={cn(
            'p-0 transition-[width] duration-300 ease-out',
            mobilePanelOpen ? '!w-72' : '!w-16',
          )}
        >
          {sidebarContent}
        </SheetContent>
      </Sheet>
    );
  }

  return <div className='row-span-2 row-start-1'>{sidebarContent}</div>;
}
